import { useRef, type RefObject } from "react";

import type { ScrubDirection } from "@/lib/timeline/playback";
import {
  isDefaultTransportPosition,
  type TransportPosition,
} from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { IconDragHandle } from "./TimelineTransportIcons";
import { TimelineTransportControls } from "./TimelineTransportControls";
import { useTransportDrag } from "./useTransportDrag";

interface TimelineTransportProps {
  stageRef: RefObject<HTMLElement | null>;
  transportSizeLevel: number;
  transportPosition: TransportPosition;
  onTransportPositionChange: (position: TransportPosition) => void;
  playing: boolean;
  speed: number;
  onPlayPause: () => void;
  onRestart: () => void;
  repeat: boolean;
  onRepeatToggle: () => void;
  onScrub: (direction: ScrubDirection) => void;
  onSkipScene: (direction: -1 | 1) => void;
  onSpeedChange: (speed: number) => void;
  cameraLockEnabled: boolean;
  autoZoomEnabled: boolean;
  onCameraLockToggle: () => void;
  onAutoZoomToggle: () => void;
  phoneSheet?: { onOpen: () => void; host: HTMLElement | null } | undefined;
}

export function TimelineTransport({
  stageRef,
  transportSizeLevel,
  transportPosition,
  onTransportPositionChange,
  playing,
  speed,
  onPlayPause,
  onRestart,
  repeat,
  onRepeatToggle,
  onScrub,
  onSkipScene,
  onSpeedChange,
  cameraLockEnabled,
  autoZoomEnabled,
  onCameraLockToggle,
  onAutoZoomToggle,
  phoneSheet,
}: TimelineTransportProps) {
  const transportRef = useRef<HTMLDivElement>(null);
  const { onHandlePointerDown } = useTransportDrag({
    stageRef,
    transportRef,
    position: transportPosition,
    onPositionChange: onTransportPositionChange,
  });

  return (
    <div
      ref={transportRef}
      className="timeline-transport"
      data-size={transportSizeLevel}
      style={
        isDefaultTransportPosition(transportPosition)
          ? { left: "1.25rem", bottom: "1.35rem", transform: "none" }
          : {
              left: `${transportPosition.x * 100}%`,
              top: `${transportPosition.y * 100}%`,
            }
      }
      role="toolbar"
      aria-label="Timeline playback"
    >
      <TimelineTransportControls
        playing={playing}
        speed={speed}
        onPlayPause={onPlayPause}
        onRestart={onRestart}
        repeat={repeat}
        onRepeatToggle={onRepeatToggle}
        onScrub={onScrub}
        onSkipScene={onSkipScene}
        onSpeedChange={onSpeedChange}
        cameraLockEnabled={cameraLockEnabled}
        autoZoomEnabled={autoZoomEnabled}
        onCameraLockToggle={onCameraLockToggle}
        onAutoZoomToggle={onAutoZoomToggle}
        phoneSheet={phoneSheet}
      />
      <button
        type="button"
        className="timeline-transport__handle"
        aria-label="Move"
        title="Move"
        onPointerDown={onHandlePointerDown}
      >
        <IconDragHandle className="timeline-transport__handle-icon" />
      </button>
    </div>
  );
}
